import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useGetA2pRegistrationQuery, useGetHomeQuery } from "@/store/rest_api";
import { A2pRegistrationStatus } from "@/model/interface/A2pRegistration";
import { MessageSquareWarning, X } from "lucide-react";
import { cn } from "@/lib/utils";

const DISMISS_KEY_PREFIX = "A2pBannerDismissed";
const REGISTRATION_LINK = "/manage-organisation/telephony?tab=sms-registration";

const STEP_LABELS: Partial<Record<A2pRegistrationStatus, string>> = {
  NOT_STARTED: "Step 1 of 4: business profile.",
  PLATFORM_REVIEW: "Step 1 of 4: our team is reviewing your submission.",
  PROFILE_PENDING: "Step 2 of 4: business profile in review.",
  PROFILE_APPROVED: "Step 3 of 4: brand registration.",
  BRAND_PENDING: "Step 3 of 4: brand in review.",
  BRAND_APPROVED: "Step 4 of 4: campaign registration.",
  CAMPAIGN_PENDING: "Step 4 of 4: campaign in review.",
};

export function A2pRegistrationBanner() {
  const { data: registration, isLoading } = useGetA2pRegistrationQuery();
  const { data: home } = useGetHomeQuery();
  const [dismissalVersion, setDismissalVersion] = useState(0);

  const dismissKey = home?.id ? `${DISMISS_KEY_PREFIX}:${home.id}` : null;
  const dismissedForStatus = dismissKey ? localStorage.getItem(dismissKey) : null;

  const handleDismiss = () => {
    if (dismissKey && registration) {
      localStorage.setItem(dismissKey, registration.status);
      setDismissalVersion(dismissalVersion + 1);
    }
  };

  if (isLoading || !registration) {
    return null;
  }
  if (registration.status === "COMPLETED") {
    return null;
  }
  if (dismissedForStatus === registration.status) {
    return null;
  }

  const rejected =
    registration.status === "PLATFORM_REJECTED" ||
    registration.status === "PROFILE_REJECTED" ||
    registration.status === "BRAND_FAILED" ||
    registration.status === "CAMPAIGN_FAILED";
  const cancelled = registration.status === "CANCELLED";
  const inReview =
    registration.status === "PLATFORM_REVIEW" ||
    registration.status === "PROFILE_PENDING" ||
    registration.status === "BRAND_PENDING" ||
    registration.status === "CAMPAIGN_PENDING";

  const title = rejected
    ? "Your text messaging registration was rejected"
    : cancelled
      ? "Your text messaging registration was cancelled"
      : inReview
        ? "Text messaging registration in review"
        : "Set up text messaging";
  const description = rejected
    ? registration.rejectionReason || "Open the registration to see the reason and resubmit."
    : cancelled
      ? "Texting stays disabled until you register again."
      : `${STEP_LABELS[registration.status] ?? ""} Texting is disabled until registration is approved.`;

  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-xl border p-4",
        rejected ? "border-destructive/50 bg-destructive/5" : "bg-card",
      )}
    >
      <div className="flex items-start gap-3">
        <MessageSquareWarning
          className={cn("mt-0.5 h-5 w-5", rejected ? "text-destructive" : "text-muted-foreground")}
        />
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button asChild size="sm" variant={rejected ? "destructive" : "default"}>
          <Link to={REGISTRATION_LINK}>
            {rejected ? "See why and fix" : cancelled ? "Restart registration" : inReview ? "View status" : "Start registration"}
          </Link>
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDismiss}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
